import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
    Briefcase, Users, CheckCircle, Clock, Loader2,
    ArrowRight, Bot, BarChart3, TrendingUp, TrendingDown,
    ArrowDown, ArrowUp, Minus, Activity, Zap, Target,
    Filter, ChevronDown,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
    { label: '7d', value: '7d' },
    { label: '30d', value: '30d' },
    { label: '90d', value: '90d' },
];

const TREND_PERIODS = [
    { label: '30d', value: '30d' },
    { label: '90d', value: '90d' },
    { label: '180d', value: '180d' },
    { label: '1y', value: '365d' },
];

const FUNNEL_COLORS = [
    'bg-blue-500', 'bg-indigo-500', 'bg-violet-500', 'bg-purple-500',
    'bg-amber-500', 'bg-orange-500', 'bg-rose-500', 'bg-teal-500',
    'bg-emerald-500', 'bg-green-500', 'bg-red-500',
];

const SOURCE_COLORS = {
    applied: '#3b82f6', matched: '#8b5cf6', endorsed: '#6366f1',
    client_added: '#f59e0b', recruiter_added: '#10b981',
    referral: '#06b6d4', import: '#f97316', unknown: '#94a3b8',
};

const TABS = [
    { key: 'overview', label: 'Overview', icon: BarChart3 },
    { key: 'funnel', label: 'Conversion Funnel', icon: Filter },
    { key: 'ttf', label: 'Time to Fill', icon: Clock },
    { key: 'agents', label: 'AI Agents', icon: Bot },
    { key: 'sources', label: 'Source Quality', icon: Target },
];

export default function ATSReports() {
    const { getAuthHeader } = useAuth();
    const [tab, setTab] = useState('overview');
    const [period, setPeriod] = useState('30d');
    const [trendPeriod, setTrendPeriod] = useState('90d');
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const [pipeline, setPipeline] = useState(null);
    const [sources, setSources] = useState(null);
    const [agentStats, setAgentStats] = useState(null);
    const [timeToFill, setTimeToFill] = useState(null);
    const [ttfTrends, setTtfTrends] = useState(null);
    const [funnelDetail, setFunnelDetail] = useState(null);
    const [agentDeepDive, setAgentDeepDive] = useState(null);
    const [sourceQuality, setSourceQuality] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        const headers = getAuthHeader();
        try {
            const [ovRes, plRes, srcRes, agRes, ttfRes] = await Promise.allSettled([
                axios.get(`${API}/ats/analytics/overview`, { headers }),
                axios.get(`${API}/ats/analytics/pipeline?period=${period}`, { headers }),
                axios.get(`${API}/ats/analytics/sources`, { headers }),
                axios.get(`${API}/ats/analytics/agent-effectiveness`, { headers }),
                axios.get(`${API}/ats/analytics/time-to-fill`, { headers }),
            ]);
            if (ovRes.status === 'fulfilled') setOverview(ovRes.value.data);
            if (plRes.status === 'fulfilled') setPipeline(plRes.value.data);
            if (srcRes.status === 'fulfilled') setSources(srcRes.value.data);
            if (agRes.status === 'fulfilled') setAgentStats(agRes.value.data);
            if (ttfRes.status === 'fulfilled') setTimeToFill(ttfRes.value.data);
        } catch {
            toast.error('Failed to load analytics');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader, period]);

    // Load advanced analytics when their tab is selected
    const loadAdvanced = useCallback(async (which) => {
        const headers = getAuthHeader();
        try {
            if (which === 'ttf') {
                const res = await axios.get(`${API}/ats/analytics/time-to-fill-trends?period=${trendPeriod}`, { headers });
                setTtfTrends(res.data);
            } else if (which === 'funnel') {
                const res = await axios.get(`${API}/ats/analytics/stage-conversion-funnel?period=${period}`, { headers });
                setFunnelDetail(res.data);
            } else if (which === 'agents') {
                const res = await axios.get(`${API}/ats/analytics/agent-deep-dive?period=${period}`, { headers });
                setAgentDeepDive(res.data);
            } else if (which === 'sources') {
                const res = await axios.get(`${API}/ats/analytics/source-quality?period=${period}`, { headers });
                setSourceQuality(res.data);
            }
        } catch { /* Advanced data optional */ }
    }, [getAuthHeader, period, trendPeriod]);

    useEffect(() => { loadData(); }, [loadData]);
    useEffect(() => { if (tab !== 'overview') loadAdvanced(tab); }, [tab, loadAdvanced]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-800 font-[Manrope]">Recruitment Reports</h1>
                    <p className="text-sm text-stone-500 mt-1">Analytics, funnels, and AI agent performance</p>
                </div>
                <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-1">
                    {PERIODS.map(p => (
                        <Button key={p.value} variant="ghost" size="sm"
                            onClick={() => setPeriod(p.value)}
                            className={`text-xs px-3 py-1.5 rounded-md ${
                                period === p.value ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                            }`}>
                            {p.label}
                        </Button>
                    ))}
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-stone-200">
                <div className="flex gap-1">
                    {TABS.map(t => (
                        <button key={t.key} onClick={() => setTab(t.key)}
                            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                                tab === t.key ? 'border-stone-900 text-stone-900' : 'border-transparent text-stone-500 hover:text-stone-700'
                            }`}>
                            <t.icon className="w-4 h-4" /> {t.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab Content */}
            {tab === 'overview' && <OverviewTab overview={overview} pipeline={pipeline} sources={sources} agentStats={agentStats} timeToFill={timeToFill} />}
            {tab === 'funnel' && <FunnelTab data={funnelDetail} period={period} />}
            {tab === 'ttf' && <TimeToFillTab data={ttfTrends} trendPeriod={trendPeriod} setTrendPeriod={setTrendPeriod} timeToFill={timeToFill} />}
            {tab === 'agents' && <AgentDeepDiveTab data={agentDeepDive} agentStats={agentStats} />}
            {tab === 'sources' && <SourceQualityTab data={sourceQuality} />}
        </div>
    );
}

// ─── Overview Tab ────────────────────────────────────────────────────────────

function OverviewTab({ overview, pipeline, sources, agentStats, timeToFill }) {
    const funnel = overview?.funnel || {};
    const funnelStages = [
        { key: 'applied', label: 'Applied' },
        { key: 'initial_interview', label: 'Interview' },
        { key: 'initial_shortlist', label: 'Shortlist' },
        { key: 'job_offer', label: 'Offer' },
        { key: 'hired', label: 'Hired' },
    ];
    const maxFunnel = Math.max(...funnelStages.map(s => funnel[s] || 0), 1);

    const sourceList = sources?.overall || [];
    const maxSource = Math.max(...sourceList.map(s => s.count || 0), 1);

    const approvalRate = agentStats?.approval_rate ?? 0;
    const circumference = 2 * Math.PI * 40;
    const strokeDashoffset = circumference - (circumference * approvalRate) / 100;

    return (
        <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <StatCard icon={Briefcase} label="Active Jobs" value={overview?.active_jobs ?? 0} iconColor="text-blue-600" iconBg="bg-blue-50" />
                <StatCard icon={Users} label="Total Applicants" value={overview?.total_applicants ?? 0} iconColor="text-indigo-600" iconBg="bg-indigo-50" />
                <StatCard icon={CheckCircle} label="Hires This Month" value={overview?.hires_this_month ?? 0} iconColor="text-emerald-600" iconBg="bg-emerald-50" accent="text-emerald-700" />
                <StatCard icon={Clock} label="Avg Time to Fill" value={overview?.avg_time_to_fill_days != null ? `${overview.avg_time_to_fill_days}d` : '—'} iconColor="text-amber-600" iconBg="bg-amber-50" />
                <StatCard icon={Bot} label="Pending AI Actions" value={overview?.pending_agent_actions ?? 0} iconColor="text-violet-600" iconBg="bg-violet-50" />
            </div>

            {/* Pipeline Funnel */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h2 className="text-lg font-semibold text-stone-800 font-[Manrope] mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-stone-400" /> Pipeline Funnel
                </h2>
                <div className="flex items-end gap-3">
                    {funnelStages.map((stage, i) => {
                        const count = funnel[stage.key] || 0;
                        const pct = maxFunnel > 0 ? (count / maxFunnel) * 100 : 0;
                        const nextCount = i < funnelStages.length - 1 ? (funnel[funnelStages[i + 1].key] || 0) : null;
                        const convRate = count > 0 && nextCount !== null ? Math.round((nextCount / count) * 100) : null;
                        return (
                            <React.Fragment key={stage.key}>
                                <div className="flex-1 flex flex-col items-center gap-2">
                                    <span className="text-lg font-bold text-stone-800">{count}</span>
                                    <div className="w-full bg-stone-100 rounded-lg relative" style={{ height: '140px' }}>
                                        <div className={`absolute bottom-0 left-0 right-0 rounded-lg ${FUNNEL_COLORS[i]} transition-all duration-500`}
                                            style={{ height: `${Math.max(pct, 8)}%` }} />
                                    </div>
                                    <span className="text-xs font-medium text-stone-600 text-center">{stage.label}</span>
                                </div>
                                {convRate !== null && (
                                    <div className="flex flex-col items-center justify-center pb-8 shrink-0">
                                        <span className="text-xs font-semibold text-stone-400">{convRate}%</span>
                                        <ArrowRight className="w-4 h-4 text-stone-300" />
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {/* Two Column */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Source Breakdown */}
                <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <h2 className="text-lg font-semibold text-stone-800 font-[Manrope] mb-4 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-stone-400" /> Source Breakdown
                    </h2>
                    {sourceList.length === 0 ? (
                        <p className="text-sm text-stone-400 text-center py-8">No source data</p>
                    ) : (
                        <div className="space-y-3">
                            {sourceList.map((src) => (
                                <div key={src.source} className="space-y-1">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-stone-700 font-medium capitalize">{src.source}</span>
                                        <span className="text-stone-500">{src.count}</span>
                                    </div>
                                    <div className="w-full h-3 bg-stone-100 rounded-full overflow-hidden">
                                        <div className="h-full rounded-full transition-all duration-500"
                                            style={{ width: `${(src.count / maxSource) * 100}%`, backgroundColor: SOURCE_COLORS[src.source] || '#94a3b8' }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Agent Effectiveness */}
                <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <h2 className="text-lg font-semibold text-stone-800 font-[Manrope] mb-4 flex items-center gap-2">
                        <Bot className="w-5 h-5 text-stone-400" /> Agent Effectiveness
                    </h2>
                    <div className="flex items-center gap-6">
                        <div className="relative shrink-0">
                            <svg width="100" height="100" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="40" fill="none" stroke="#f5f5f4" strokeWidth="8" />
                                <circle cx="50" cy="50" r="40" fill="none" stroke="#10b981" strokeWidth="8"
                                    strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
                                    transform="rotate(-90 50 50)" className="transition-all duration-700" />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-lg font-bold text-stone-800">{Math.round(approvalRate)}%</span>
                            </div>
                        </div>
                        <div className="space-y-2 flex-1">
                            <MetricRow label="Auto-Executed" value={agentStats?.total_executed ?? 0} />
                            <MetricRow label="Pending Review" value={agentStats?.total_pending ?? 0} />
                            <MetricRow label="Approved" value={agentStats?.total_approved ?? 0} color="text-emerald-600" />
                            <MetricRow label="Rejected" value={agentStats?.total_rejected ?? 0} color="text-rose-600" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Conversion Funnel Tab ───────────────────────────────────────────────────

function FunnelTab({ data, period }) {
    if (!data) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

    const funnel = data.funnel || [];
    const maxCount = Math.max(...funnel.map(f => f.count), 1);

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h2 className="text-lg font-semibold text-stone-800 font-[Manrope] mb-1">Stage Conversion Funnel</h2>
                <p className="text-sm text-stone-400 mb-6">{data.total_applications} total applications in {period} period</p>

                <div className="space-y-1">
                    {funnel.filter(f => f.count > 0 || ['applied', 'hired', 'unsuccessful'].includes(f.stage)).map((stage, i) => {
                        const widthPct = Math.max((stage.count / maxCount) * 100, 4);
                        const isTerminal = ['hired', 'unsuccessful'].includes(stage.stage);
                        return (
                            <div key={stage.stage} className="group">
                                <div className="flex items-center gap-4">
                                    <div className="w-36 text-right">
                                        <p className="text-sm font-medium text-stone-700">{stage.label}</p>
                                        <p className="text-xs text-stone-400">{stage.avg_days_in_stage != null ? `${stage.avg_days_in_stage}d avg` : ''}</p>
                                    </div>
                                    <div className="flex-1">
                                        <div className="relative h-10 bg-stone-50 rounded-lg overflow-hidden">
                                            <div className={`absolute left-0 top-0 bottom-0 rounded-lg transition-all duration-700 ${
                                                isTerminal && stage.stage === 'unsuccessful' ? 'bg-red-400' :
                                                isTerminal ? 'bg-emerald-500' : FUNNEL_COLORS[i % FUNNEL_COLORS.length]
                                            }`} style={{ width: `${widthPct}%` }}>
                                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-white">
                                                    {stage.count}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="w-24 text-right">
                                        {stage.conversion_rate > 0 && i > 0 && (
                                            <span className={`text-xs font-semibold ${stage.conversion_rate >= 50 ? 'text-emerald-600' : stage.conversion_rate >= 25 ? 'text-amber-600' : 'text-rose-600'}`}>
                                                {stage.conversion_rate}% conv
                                            </span>
                                        )}
                                    </div>
                                    <div className="w-24 text-right">
                                        {stage.dropoff_rate > 0 && i > 0 && (
                                            <span className="text-xs text-stone-400">
                                                {stage.dropoff_rate}% drop
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Stage Velocity Table */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h2 className="text-lg font-semibold text-stone-800 font-[Manrope] mb-4">Stage Velocity</h2>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-stone-200">
                                <th className="text-left py-2 px-3 text-stone-500 font-medium">Stage</th>
                                <th className="text-right py-2 px-3 text-stone-500 font-medium">Count</th>
                                <th className="text-right py-2 px-3 text-stone-500 font-medium">Avg Days</th>
                                <th className="text-right py-2 px-3 text-stone-500 font-medium">Median Days</th>
                                <th className="text-right py-2 px-3 text-stone-500 font-medium">Conversion</th>
                                <th className="text-right py-2 px-3 text-stone-500 font-medium">Drop-off</th>
                            </tr>
                        </thead>
                        <tbody>
                            {funnel.filter(f => f.count > 0).map(stage => (
                                <tr key={stage.stage} className="border-b border-stone-50 hover:bg-stone-50">
                                    <td className="py-2.5 px-3 font-medium text-stone-800">{stage.label}</td>
                                    <td className="py-2.5 px-3 text-right text-stone-600">{stage.count}</td>
                                    <td className="py-2.5 px-3 text-right">
                                        {stage.avg_days_in_stage != null ? (
                                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                                stage.avg_days_in_stage <= 3 ? 'bg-emerald-50 text-emerald-700' :
                                                stage.avg_days_in_stage <= 7 ? 'bg-amber-50 text-amber-700' : 'bg-rose-50 text-rose-700'
                                            }`}>{stage.avg_days_in_stage}d</span>
                                        ) : '—'}
                                    </td>
                                    <td className="py-2.5 px-3 text-right text-stone-500">{stage.median_days_in_stage ?? '—'}{stage.median_days_in_stage != null && 'd'}</td>
                                    <td className="py-2.5 px-3 text-right text-emerald-600 font-medium">{stage.conversion_rate > 0 ? `${stage.conversion_rate}%` : '—'}</td>
                                    <td className="py-2.5 px-3 text-right text-stone-400">{stage.dropoff_rate > 0 ? `${stage.dropoff_rate}%` : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// ─── Time to Fill Tab ────────────────────────────────────────────────────────

function TimeToFillTab({ data, trendPeriod, setTrendPeriod, timeToFill }) {
    return (
        <div className="space-y-6">
            {/* Trend Chart */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 className="text-lg font-semibold text-stone-800 font-[Manrope]">Time to Fill Trends</h2>
                        {data && (
                            <div className="flex items-center gap-3 mt-1">
                                <span className="text-sm text-stone-500">Current avg: <span className="font-semibold text-stone-800">{data.current_avg ?? '—'}d</span></span>
                                {data.previous_avg && (
                                    <span className={`flex items-center gap-1 text-xs font-medium ${
                                        data.trend_direction === 'improving' ? 'text-emerald-600' :
                                        data.trend_direction === 'worsening' ? 'text-rose-600' : 'text-stone-400'
                                    }`}>
                                        {data.trend_direction === 'improving' ? <ArrowDown className="w-3 h-3" /> :
                                         data.trend_direction === 'worsening' ? <ArrowUp className="w-3 h-3" /> :
                                         <Minus className="w-3 h-3" />}
                                        vs prev {data.previous_avg}d
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-1">
                        {TREND_PERIODS.map(p => (
                            <Button key={p.value} variant="ghost" size="sm"
                                onClick={() => setTrendPeriod(p.value)}
                                className={`text-xs px-2.5 py-1 rounded-md ${
                                    trendPeriod === p.value ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500'
                                }`}>{p.label}</Button>
                        ))}
                    </div>
                </div>

                {/* Simple bar chart for trends */}
                {data?.trends?.length > 0 ? (
                    <div className="flex items-end gap-1 h-48">
                        {data.trends.map((t, i) => {
                            const maxDays = Math.max(...data.trends.map(tr => tr.max_days || tr.avg_days), 1);
                            const heightPct = (t.avg_days / maxDays) * 100;
                            return (
                                <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-stone-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                        {t.avg_days}d avg ({t.count} jobs)
                                    </div>
                                    <div className="w-full bg-stone-100 rounded-t-md relative" style={{ height: '100%' }}>
                                        <div className={`absolute bottom-0 left-0 right-0 rounded-t-md transition-all duration-500 ${
                                            t.avg_days <= 30 ? 'bg-emerald-400' : t.avg_days <= 60 ? 'bg-amber-400' : 'bg-rose-400'
                                        }`} style={{ height: `${Math.max(heightPct, 5)}%` }} />
                                    </div>
                                    <span className="text-[10px] text-stone-400 truncate w-full text-center">{t.label.split('-').pop()}</span>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-sm text-stone-400 text-center py-12">No trend data available for this period</p>
                )}
            </div>

            {/* Per-Job TTF Table */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h2 className="text-lg font-semibold text-stone-800 font-[Manrope] mb-4">Per-Job Time to Fill</h2>
                {(!timeToFill?.jobs || timeToFill.jobs.length === 0) ? (
                    <p className="text-sm text-stone-400 text-center py-8">No completed jobs yet</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-stone-200">
                                    <th className="text-left py-2 px-3 text-stone-500 font-medium">Job Title</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium">Days to Fill</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium">Applicants</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {timeToFill.jobs.map(job => (
                                    <tr key={job.job_id} className="border-b border-stone-50 hover:bg-stone-50">
                                        <td className="py-2.5 px-3 text-stone-800 font-medium">{job.title}</td>
                                        <td className="py-2.5 px-3 text-right">
                                            {job.days_to_fill != null ? (
                                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                                    job.days_to_fill <= 30 ? 'bg-emerald-50 text-emerald-700' :
                                                    job.days_to_fill <= 60 ? 'bg-amber-50 text-amber-700' : 'bg-rose-50 text-rose-700'
                                                }`}>{job.days_to_fill} days</span>
                                            ) : <span className="text-stone-400">—</span>}
                                        </td>
                                        <td className="py-2.5 px-3 text-right text-stone-600">{job.total_applicants}</td>
                                        <td className="py-2.5 px-3 text-right">
                                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                                job.days_to_fill != null ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'
                                            }`}>{job.days_to_fill != null ? 'Filled' : 'Open'}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            {timeToFill.avg_days_to_fill != null && (
                                <tfoot>
                                    <tr className="border-t border-stone-200 bg-stone-50">
                                        <td className="py-2.5 px-3 font-semibold text-stone-700">Average</td>
                                        <td className="py-2.5 px-3 text-right font-semibold text-stone-700">{Math.round(timeToFill.avg_days_to_fill)} days</td>
                                        <td colSpan={2} />
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Agent Deep Dive Tab ─────────────────────────────────────────────────────

function AgentDeepDiveTab({ data, agentStats }) {
    if (!data) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

    // Group action details by agent
    const agentActionMap = {};
    (data.action_details || []).forEach(d => {
        if (!agentActionMap[d.agent]) agentActionMap[d.agent] = [];
        agentActionMap[d.agent].push(d);
    });

    const dailyTrend = data.daily_trend || [];
    const maxDaily = Math.max(...dailyTrend.map(d =>
        (d.executed || 0) + (d.pending_review || 0) + (d.approved || 0) + (d.rejected || 0)
    ), 1);

    return (
        <div className="space-y-6">
            {/* Review Response Time */}
            {data.review_times?.length > 0 && (
                <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <h2 className="text-lg font-semibold text-stone-800 font-[Manrope] mb-4 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-stone-400" /> Review Response Time
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {data.review_times.map(rt => (
                            <div key={rt.agent} className="bg-stone-50 rounded-lg p-4">
                                <p className="text-xs text-stone-400 uppercase font-medium mb-1">{rt.agent.replace(/_/g, ' ')}</p>
                                <p className="text-2xl font-bold text-stone-800">{rt.avg_hours}h</p>
                                <p className="text-xs text-stone-400 mt-1">{rt.min_hours}h min · {rt.max_hours}h max · {rt.reviews} reviews</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Daily Action Volume */}
            {dailyTrend.length > 0 && (
                <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <h2 className="text-lg font-semibold text-stone-800 font-[Manrope] mb-4 flex items-center gap-2">
                        <Activity className="w-5 h-5 text-stone-400" /> Daily Action Volume
                    </h2>
                    <div className="flex items-end gap-0.5 h-32">
                        {dailyTrend.map((d, i) => {
                            const total = (d.executed || 0) + (d.pending_review || 0) + (d.approved || 0) + (d.rejected || 0);
                            const heightPct = (total / maxDaily) * 100;
                            return (
                                <div key={i} className="flex-1 group relative">
                                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-stone-900 text-white text-xs px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                                        {d.date}: {total} actions
                                    </div>
                                    <div className="w-full bg-stone-100 rounded-t" style={{ height: `${Math.max(heightPct, 3)}%` }}>
                                        <div className="w-full h-full bg-violet-400 rounded-t" />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Per-Agent Action Breakdown */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h2 className="text-lg font-semibold text-stone-800 font-[Manrope] mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-stone-400" /> Action Breakdown by Agent
                </h2>
                {Object.keys(agentActionMap).length === 0 ? (
                    <p className="text-sm text-stone-400 text-center py-8">No agent actions in this period</p>
                ) : (
                    <div className="space-y-6">
                        {Object.entries(agentActionMap).map(([agent, actions]) => (
                            <div key={agent}>
                                <h3 className="text-sm font-semibold text-stone-700 capitalize mb-2">{agent.replace(/_/g, ' ')}</h3>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-stone-100">
                                                <th className="text-left py-1.5 px-2 text-stone-400 text-xs font-medium">Action</th>
                                                <th className="text-right py-1.5 px-2 text-stone-400 text-xs font-medium">Status</th>
                                                <th className="text-right py-1.5 px-2 text-stone-400 text-xs font-medium">Count</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {actions.map((a, i) => (
                                                <tr key={i} className="border-b border-stone-50">
                                                    <td className="py-1.5 px-2 text-stone-700">{a.action_type.replace(/_/g, ' ')}</td>
                                                    <td className="py-1.5 px-2 text-right">
                                                        <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                                                            a.status === 'executed' ? 'bg-blue-50 text-blue-600' :
                                                            a.status === 'approved' ? 'bg-emerald-50 text-emerald-600' :
                                                            a.status === 'rejected' ? 'bg-rose-50 text-rose-600' :
                                                            'bg-amber-50 text-amber-600'
                                                        }`}>{a.status}</span>
                                                    </td>
                                                    <td className="py-1.5 px-2 text-right font-medium text-stone-800">{a.count}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Matching Pipeline Stats */}
            {data.matching_pipeline_stats?.length > 0 && (
                <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <h2 className="text-lg font-semibold text-stone-800 font-[Manrope] mb-4 flex items-center gap-2">
                        <Target className="w-5 h-5 text-stone-400" /> Matching Pipeline Runs
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {data.matching_pipeline_stats.map(ps => (
                            <div key={ps.status} className="bg-stone-50 rounded-lg p-4">
                                <p className="text-xs text-stone-400 uppercase font-medium mb-1">{ps.status} runs</p>
                                <p className="text-2xl font-bold text-stone-800">{ps.count}</p>
                                <div className="mt-2 space-y-1 text-xs text-stone-500">
                                    <p>Avg evaluated: {ps.avg_evaluated}</p>
                                    <p>Avg matches: {ps.avg_matches}</p>
                                    <p>Avg activated: {ps.avg_activated}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Top Rejection Reasons */}
            {data.rejection_reasons?.length > 0 && (
                <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <h2 className="text-lg font-semibold text-stone-800 font-[Manrope] mb-4">Top Rejection Reasons</h2>
                    <div className="space-y-2">
                        {data.rejection_reasons.slice(0, 10).map((r, i) => (
                            <div key={i} className="flex items-center gap-3 text-sm">
                                <span className="text-xs text-stone-400 w-16 capitalize">{r.agent.replace(/_/g, ' ')}</span>
                                <span className="flex-1 text-stone-700">{r.feedback}</span>
                                <span className="text-xs font-medium text-stone-500">{r.count}x</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Source Quality Tab ──────────────────────────────────────────────────────

function SourceQualityTab({ data }) {
    if (!data) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

    const sources = data.sources || [];

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h2 className="text-lg font-semibold text-stone-800 font-[Manrope] mb-1">Source Quality Analysis</h2>
                <p className="text-sm text-stone-400 mb-6">Conversion rates by application source over {data.period}</p>

                {sources.length === 0 ? (
                    <p className="text-sm text-stone-400 text-center py-8">No source data available</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-stone-200">
                                    <th className="text-left py-2 px-3 text-stone-500 font-medium">Source</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium">Total</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium">→ Interview</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium">→ Shortlist</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium">→ Offer</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium">→ Hired</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium">Avg Match</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sources.map(src => (
                                    <tr key={src.source} className="border-b border-stone-50 hover:bg-stone-50">
                                        <td className="py-3 px-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SOURCE_COLORS[src.source] || '#94a3b8' }} />
                                                <span className="font-medium text-stone-800 capitalize">{src.source}</span>
                                            </div>
                                        </td>
                                        <td className="py-3 px-3 text-right font-semibold text-stone-800">{src.total}</td>
                                        <td className="py-3 px-3 text-right"><RateBadge rate={src.interview_rate} /></td>
                                        <td className="py-3 px-3 text-right"><RateBadge rate={src.shortlist_rate} /></td>
                                        <td className="py-3 px-3 text-right"><RateBadge rate={src.offer_rate} /></td>
                                        <td className="py-3 px-3 text-right"><RateBadge rate={src.hire_rate} highlight /></td>
                                        <td className="py-3 px-3 text-right">
                                            {src.avg_match_score != null ? (
                                                <span className={`text-xs font-semibold ${
                                                    src.avg_match_score >= 70 ? 'text-emerald-600' :
                                                    src.avg_match_score >= 50 ? 'text-amber-600' : 'text-stone-400'
                                                }`}>{src.avg_match_score}%</span>
                                            ) : <span className="text-stone-300">—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Visual comparison bars */}
            {sources.length > 0 && (
                <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <h2 className="text-lg font-semibold text-stone-800 font-[Manrope] mb-4">Hire Rate Comparison</h2>
                    <div className="space-y-3">
                        {sources.filter(s => s.total >= 1).sort((a, b) => b.hire_rate - a.hire_rate).map(src => (
                            <div key={src.source} className="space-y-1">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-stone-700 font-medium capitalize">{src.source}</span>
                                    <span className="text-stone-500">{src.hire_rate}% ({src.total} total)</span>
                                </div>
                                <div className="w-full h-4 bg-stone-100 rounded-full overflow-hidden">
                                    <div className="h-full rounded-full transition-all duration-500"
                                        style={{
                                            width: `${Math.max(src.hire_rate, 1)}%`,
                                            backgroundColor: SOURCE_COLORS[src.source] || '#94a3b8',
                                        }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Shared Components ───────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, iconColor, iconBg, accent }) {
    return (
        <div className="bg-white rounded-xl border border-stone-200 p-5">
            <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${iconColor}`} />
                </div>
                <div>
                    <p className="text-sm text-stone-500">{label}</p>
                    <p className={`text-xl font-bold ${accent || 'text-stone-800'} font-[Manrope]`}>{value}</p>
                </div>
            </div>
        </div>
    );
}

function MetricRow({ label, value, color }) {
    return (
        <div className="flex justify-between text-sm">
            <span className="text-stone-500">{label}</span>
            <span className={`font-semibold ${color || 'text-stone-800'}`}>{value}</span>
        </div>
    );
}

function RateBadge({ rate, highlight }) {
    if (rate == null || rate === 0) return <span className="text-stone-300">—</span>;
    const colorClass = highlight
        ? (rate >= 10 ? 'text-emerald-700 bg-emerald-50' : rate >= 5 ? 'text-amber-700 bg-amber-50' : 'text-stone-600 bg-stone-50')
        : (rate >= 50 ? 'text-emerald-600' : rate >= 25 ? 'text-amber-600' : 'text-stone-500');

    return highlight ? (
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${colorClass}`}>{rate}%</span>
    ) : (
        <span className={`text-xs font-medium ${colorClass}`}>{rate}%</span>
    );
}
